export type SignLanguageId = "psl" | "asl";

export interface SignLetter {
  /** Stable id; for ASL this is the recognition model's label ("a"…"z"). */
  id: string;
  /** What the learner sees: "ب" or "B". */
  label: string;
  /** Spoken name, e.g. "Bay". */
  name: string;
  image?: string;
  video?: string;
  /** Still frame shown before a moving-letter video plays. */
  poster?: string;
  /** The sign involves movement, so a still-handshape model can't score it. */
  moves?: boolean;
}

export interface SignLanguage {
  id: SignLanguageId;
  short: string;
  name: string;
  country: string;
  script: string;
  dir: "rtl" | "ltr";
  speechLang: string;
  letters: SignLetter[];
  dictionaries: { name: string; url: string }[];
  credit?: { text: string; url: string };
  /** Has photos of the number signs 0–9 (for Number Signs). */
  numbers?: boolean;
  /** Letter pairs signed with the same handshape (the camera cannot tell them apart). */
  lookalikes?: [string, string][];
}

const psl = (id: string, label: string, name: string, moves = false): SignLetter =>
  moves
    ? { id, label, name, moves, video: `/psl/${id}.mp4`, poster: `/psl/${id}-poster.jpg` }
    : { id, label, name, image: `/psl/${id}.jpg` };

/** Urdu alphabet in the order used by the UAlpha40 PSL dataset. */
const PSL_LETTERS: SignLetter[] = [
  psl("alif-madd", "آ", "Alif madd", true),
  psl("alif", "ا", "Alif"),
  psl("bay", "ب", "Bay"),
  psl("pay", "پ", "Pay"),
  psl("tay", "ت", "Tay"),
  psl("ttay", "ٹ", "Ṭay"),
  psl("say", "ث", "Say"),
  psl("jeem", "ج", "Jeem", true),
  psl("chay", "چ", "Chay"),
  psl("hay-hutti", "ح", "Hay (ḥ)"),
  psl("khay", "خ", "Khay"),
  psl("dal", "د", "Daal"),
  psl("ddal", "ڈ", "Ḍaal"),
  psl("zaal", "ذ", "Zaal"),
  psl("ray", "ر", "Ray"),
  psl("rray", "ڑ", "Ṛay", true),
  psl("zay", "ز", "Zay"),
  psl("zhay", "ژ", "Zhay"),
  psl("seen", "س", "Seen"),
  psl("sheen", "ش", "Sheen"),
  psl("suad", "ص", "Suad"),
  psl("zuad", "ض", "Zuad"),
  psl("toay", "ط", "Toay"),
  psl("zoay", "ظ", "Zoay"),
  psl("ain", "ع", "Ain"),
  psl("ghain", "غ", "Ghain"),
  psl("fay", "ف", "Fay"),
  psl("qaf", "ق", "Qaaf"),
  psl("kaf", "ک", "Kaaf"),
  psl("gaf", "گ", "Gaaf"),
  psl("lam", "ل", "Laam"),
  psl("meem", "م", "Meem"),
  psl("noon", "ن", "Noon"),
  psl("noon-ghunna", "ں", "Noon ghunna"),
  psl("wao", "و", "Wao"),
  psl("hay-gol", "ہ", "Choti hay", true),
  psl("do-chashmi-hay", "ھ", "Do-chashmi hay"),
  psl("hamza", "ء", "Hamza"),
  psl("choti-yay", "ی", "Choti yay"),
  psl("bari-yay", "ے", "Baṛi yay"),
];

const ASL_LETTERS: SignLetter[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((c) => ({
  id: c.toLowerCase(),
  label: c,
  name: c,
  image: `/asl/${c}.png`,
}));

export const SIGN_LANGUAGES: Record<SignLanguageId, SignLanguage> = {
  psl: {
    id: "psl",
    short: "PSL",
    name: "Pakistan Sign Language",
    country: "Pakistan",
    script: "Urdu",
    dir: "rtl",
    speechLang: "ur-PK",
    letters: PSL_LETTERS,
    dictionaries: [
      { name: "PSL dictionary (FESF / Deaf Reach)", url: "https://psl.org.pk" },
      { name: "SpreadTheSign", url: "https://www.spreadthesign.com" },
    ],
    lookalikes: [
      ["toay", "ddal"],
      ["zoay", "zuad"],
    ],
    credit: {
      text: "Alphabet photos and videos: UAlpha40 dataset (Bahria University), CC BY 4.0, resized.",
      url: "https://data.mendeley.com/datasets/3pvnnckxyb/3",
    },
  },
  asl: {
    id: "asl",
    short: "ASL",
    name: "American Sign Language",
    country: "United States",
    script: "English",
    dir: "ltr",
    speechLang: "en-US",
    letters: ASL_LETTERS,
    dictionaries: [
      { name: "Lifeprint ASL dictionary", url: "https://www.lifeprint.com" },
      { name: "SpreadTheSign", url: "https://www.spreadthesign.com" },
    ],
    numbers: true,
    credit: {
      text: "Number photos: ASL-HG dataset (Pranto et al., Bangladesh University of Business and Technology), CC BY 4.0.",
      url: "https://data.mendeley.com/datasets/j4y5w2c8w9/1",
    },
  },
};

export const DEFAULT_SIGN_LANGUAGE: SignLanguageId = "psl";

/** Shown in the language picker but not selectable yet (two-handed alphabets). */
export const COMING_SOON_LANGUAGES = [
  { short: "ISL", name: "Indian Sign Language" },
  { short: "BSL", name: "British Sign Language" },
];

export const getSignLanguage = (id: SignLanguageId | undefined): SignLanguage =>
  SIGN_LANGUAGES[id ?? DEFAULT_SIGN_LANGUAGE] ?? SIGN_LANGUAGES[DEFAULT_SIGN_LANGUAGE];

export const staticLetters = (lang: SignLanguage) => lang.letters.filter((l) => !l.moves);

export const findLetter = (lang: SignLanguage, id: string | undefined) =>
  lang.letters.find((l) => l.id === id);

/** Letters signed with the same handshape as `id`. */
export const sameHandshapeAs = (lang: SignLanguage, id: string): string[] =>
  (lang.lookalikes ?? []).flatMap(([a, b]) => (a === id ? [b] : b === id ? [a] : []));

/** Arabic-script variants people type that should count as the Urdu letter. */
const URDU_VARIANTS: Record<string, string> = {
  "ك": "ک",
  "ي": "ی",
  "ى": "ی",
  "ه": "ہ",
  "ة": "ہ",
  "ۀ": "ہ",
  "أ": "ا",
  "إ": "ا",
  "ٱ": "ا",
  "ۓ": "ے",
  "ؤ": "و",
  "ئ": "ء",
  "ۂ": "ہ",
  "ۃ": "ہ",
};

// Harakat, tanween, superscript alif, Urdu tatweel.
const DIACRITICS = /[\u064B-\u065F\u0670\u0640]/g;
// Zero-width joiners/non-joiners, direction marks and isolates, BOM — common in typed or pasted Urdu.
const INVISIBLE = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;
// Urdu and Latin punctuation: treated as word breaks, never as letters to sign.
const PUNCTUATION = /[\u060C\u061B\u061F\u06D4\u066B\u066C!?.,;:"'()[\]{}«»…\-–—]/g;

/** Canonical form used for spelling and search: variants unified, marks and invisible characters removed. */
export function normaliseSearch(lang: SignLanguage, text: string) {
  const cleaned = text.replace(INVISIBLE, "");
  if (lang.script === "Urdu") {
    return Array.from(cleaned.normalize("NFC").replace(DIACRITICS, ""))
      .map((c) => URDU_VARIANTS[c] ?? c)
      .join("");
  }
  return cleaned.toLowerCase();
}

export interface SpelledWord {
  word: string;
  letters: (SignLetter | null)[];
}

/** Splits text into words and maps every character to a fingerspelling letter (null if none). */
export function spell(lang: SignLanguage, text: string): SpelledWord[] {
  const byLabel = new Map<string, SignLetter>();
  for (const l of lang.letters) byLabel.set(lang.script === "Urdu" ? l.label : l.id, l);
  return normaliseSearch(lang, text)
    .replace(PUNCTUATION, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => ({ word, letters: Array.from(word).map((c) => byLabel.get(c) ?? null) }));
}

export function lettersOf(lang: SignLanguage, word: string): string[] {
  return spell(lang, word)
    .flatMap((w) => w.letters)
    .filter((l): l is SignLetter => l !== null)
    .map((l) => l.id);
}
